import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from "@mui/material";
import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createHealthCheckForService } from "../api/client";
import { Panel } from "../components/layout/Panel";
import { PageErrorState } from "../components/pages/PageErrorState";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import { Select, MenuItem, InputLabel, FormControl } from "@mui/material";

export function PageHealthCheckNew() {
  const navigate = useNavigate();
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;

  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [probeType, setProbeType] = useState<"liveness" | "liveness_dotnet_health">("liveness");
  const [probeIntervalMS, setProbeIntervalMS] = useState(10000);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!serviceSlug) return;

    if (!name.trim() || !url.trim()) {
      setError("Name and URL are required");
      return;
    }

    if (probeIntervalMS < 1000) {
      setError("Interval must be at least 1000ms (1 second)");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await createHealthCheckForService(
        serviceSlug,
        {
          name: name.trim(),
          url: url.trim(),
          probe_type: probeType,
          probe_interval_ms: probeIntervalMS,
          enabled,
        },
        undefined,
      );
      navigate(`/services/${encodeURIComponent(serviceSlug)}/health-checks`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create health check");
    } finally {
      setLoading(false);
    }
  };

  if (!serviceSlug) {
    return (
      <Alert severity="warning">
        Missing service in URL. Please navigate from a service overview page.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Panel
        title="Create Health Check"
        subtitle={`Service: ${serviceSlug}`}
        toolbar={
          <Stack direction="row" spacing={1}>
            <Button variant="text" onClick={() => navigate(-1)} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              form="health-check-new-form"
              disabled={loading}
            >
              Create
            </Button>
          </Stack>
        }
      >
        {error ? <PageErrorState message={error} sx={{ mb: 1.5 }} /> : null}
        <Box
          id="health-check-new-form"
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            maxWidth: 720,
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="API Health Check"
          />
          <TextField
            fullWidth
            size="small"
            label="URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
            placeholder="https://api.example.com/health"
            type="url"
          />
          <FormControl fullWidth size="small">
            <InputLabel>Probe Type</InputLabel>
            <Select
              value={probeType}
              label="Probe Type"
              onChange={(e) => setProbeType(e.target.value as "liveness" | "liveness_dotnet_health")}
            >
              <MenuItem value="liveness">Liveness (HTTP Status Code)</MenuItem>
              <MenuItem value="liveness_dotnet_health">Liveness (.NET Health Check)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label="Probe Interval (milliseconds)"
            value={probeIntervalMS}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              if (!isNaN(val)) setProbeIntervalMS(val);
            }}
            type="number"
            inputProps={{ min: 1000, step: 1000 }}
            helperText={`${Math.round(probeIntervalMS / 1000)} seconds`}
          />
          <FormControlLabel
            control={
              <Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            }
            label="Enabled"
          />
          {probeType === "liveness_dotnet_health" && (
            <Alert severity="info" sx={{ mt: 1 }}>
              This will parse the .NET health check JSON response and track main status plus subsystem
              statuses (e.g., sqlserver, redis).
            </Alert>
          )}
        </Box>
      </Panel>
    </Box>
  );
}
